// ---------------------------------------------------------------------------
// Typed API helpers for dev-console endpoints.
// Each function wraps the low-level `api()` call with proper types and URL construction.
// ---------------------------------------------------------------------------
import type {
  EnrollmentAutomationUpdateRequest,
  EnrollmentAutomationUpdateResponse,
  EnrollmentAutomationsResponse,
} from '@dev-console/enrollment-automations';
import type {
  PatientAnnouncementBannerConfigResponse,
  PatientAnnouncementBannerUpdateRequest,
  PatientAnnouncementBannerUpdateResponse,
} from '@dev-console/patient-announcement-banner';
import type {
  ProviderAnnouncementBannerConfigResponse,
  ProviderAnnouncementBannerUpdateRequest,
  ProviderAnnouncementBannerUpdateResponse,
} from '@dev-console/provider-announcement-banner';
import type {
  DeploymentCancelRequest,
  DeploymentCancelResponse,
  DeploymentCurrentResponse,
  DeploymentEnvironment,
  DeploymentPipelineRequest,
  DeploymentPipelineResponse,
  DeploymentPipelineStatusResponse,
  DeploymentHistoryResponse,
  DeploymentMode,
  DeploymentRollbackPreflightRequest,
  DeploymentRollbackPreflightResponse,
  DeploymentRollbackRequest,
  DeploymentRollbackResponse,
  DeploymentStatsDays,
  DeploymentStatsResponse,
  DevexCodexFeedbackPullRequestState,
  DevexCodexFeedbackReactionFilter,
  DevexCodexFeedbackResponse,
  DevexReviewLatencyGroupBy,
  DevexReviewLatencyResponse,
  DevexReviewLatencyScope,
  DevexReviewLatencySizeBucket,
  DiscardResult,
  DlqMessagesResponse,
  DownstreamHealthResponse,
  EntityLookup,
  JobDetailResponse,
  JobLogEntry,
  JobScheduleListResponse,
  JobScheduleSetEnabledRequest,
  JobScheduleSetEnabledResponse,
  JobsDbEnvironmentQuery,
  JobSearchFilters,
  MergeFreezeRequest,
  MergeFreezeResponse,
  MergeFreezeStatusResponse,
  MobileAppDeploymentApp,
  MobileAppDeploymentListResponse,
  MobileAppDeploymentPlatform,
  MobileAppDeploymentTypeFilter,
  PaginatedResponse,
  PatientActivityExportFormat,
  PatientActivityExportSummary,
  PatientAppointment,
  PatientCareTeamAssignment,
  PatientChatSession,
  PatientDetail,
  PatientListItem,
  PatientSignUpData,
  PostgresJobFilters,
  PostgresJobDiscardResult,
  PostgresJobListResponse,
  PostgresJobNamesResponse,
  PostgresJobRedriveResult,
  PostgresJobsDiscardResult,
  PostgresJobsRedriveResult,
  ProviderCalendarEventsData,
  ProviderCalendarSettings,
  ProviderCredential,
  ProviderDetail,
  ProviderExternalCalendarData,
  ProviderLicense,
  ProviderListItem,
  QueueOverview,
  RedriveRequest,
  RedriveResult,
  RunbookMapping,
  RunbookSearchResult,
  SmartRedriveInfo,
  WorkflowRunDetailResponse,
  WorkflowRunsResponse,
} from '@dev-console/types';

import { api, apiBlob } from '@/config/api';

// ---------------------------------------------------------------------------
// Patients
// ---------------------------------------------------------------------------

export function searchPatients(params: { query?: string; page?: number; pageSize?: number }) {
  const sp = new URLSearchParams();
  if (params.page != null) sp.set('page', String(params.page));
  if (params.pageSize != null) sp.set('pageSize', String(params.pageSize));
  if (params.query?.trim()) sp.set('query', params.query.trim());
  return api<PaginatedResponse<PatientListItem>>(`/dev-console/patients?${sp}`);
}

export function getPatient(patientId: number | string) {
  return api<PatientDetail>(`/dev-console/patients/${patientId}`);
}

export function getPatientCareTeam(patientId: number) {
  return api<PatientCareTeamAssignment[]>(`/dev-console/patients/${patientId}/care-team`);
}

export function getPatientAppointments(patientId: number) {
  return api<PatientAppointment[]>(`/dev-console/patients/${patientId}/appointments`);
}

export function getPatientAiChat(patientId: number) {
  return api<PatientChatSession[]>(`/dev-console/patients/${patientId}/ai-chat`);
}

export function getPatientSignUp(patientId: number) {
  return api<PatientSignUpData>(`/dev-console/patients/${patientId}/sign-up`);
}

export function getPatientActivityExportSummary(patientId: number) {
  return api<PatientActivityExportSummary>(`/dev-console/patients/${patientId}/activity-export/summary`);
}

export function downloadPatientActivityExport(patientId: number, format: PatientActivityExportFormat) {
  return apiBlob(`/dev-console/patients/${patientId}/activity-export/download`, {
    method: 'POST',
    body: JSON.stringify({ format }),
  });
}

// ---------------------------------------------------------------------------
// Providers
// ---------------------------------------------------------------------------

export function searchProviders(params: { query?: string; page?: number; pageSize?: number }) {
  const sp = new URLSearchParams();
  if (params.page != null) sp.set('page', String(params.page));
  if (params.pageSize != null) sp.set('pageSize', String(params.pageSize));
  if (params.query?.trim()) sp.set('query', params.query.trim());
  return api<PaginatedResponse<ProviderListItem>>(`/dev-console/providers?${sp}`);
}

export function getProvider(providerId: number | string) {
  return api<ProviderDetail>(`/dev-console/providers/${providerId}`);
}

export function getProviderCredentials(providerId: number) {
  return api<ProviderCredential[]>(`/dev-console/providers/${providerId}/credentials`);
}

export function getProviderCalendarSettings(providerId: number) {
  return api<ProviderCalendarSettings>(`/dev-console/providers/${providerId}/calendar-settings`);
}

export function getProviderCalendarEvents(providerId: number, params: { start: string; end: string }) {
  const sp = new URLSearchParams({ start: params.start, end: params.end });
  return api<ProviderCalendarEventsData>(`/dev-console/providers/${providerId}/calendar-events?${sp}`);
}

export function getProviderExternalCalendar(providerId: number) {
  return api<ProviderExternalCalendarData>(`/dev-console/providers/${providerId}/external-calendar`);
}

export function getProviderLicenses(providerId: number) {
  return api<ProviderLicense[]>(`/dev-console/providers/${providerId}/licenses`);
}

// ---------------------------------------------------------------------------
// Jobs
// ---------------------------------------------------------------------------

export function getJobQueues() {
  return api<QueueOverview[]>('/dev-console/jobs/queues');
}

export function getJobDlq(canonicalJob: string, params: { limit?: number } = {}) {
  const sp = new URLSearchParams();
  if (params.limit != null) sp.set('limit', String(params.limit));
  const qs = sp.toString();
  return api<DlqMessagesResponse>(
    `/dev-console/jobs/queues/${encodeURIComponent(canonicalJob)}/dlq${qs ? `?${qs}` : ''}`
  );
}

export function getSmartRedriveInfo(canonicalJob: string) {
  return api<SmartRedriveInfo>(`/dev-console/jobs/queues/${encodeURIComponent(canonicalJob)}/smart-redrive-info`);
}

export function redriveJobs(body: RedriveRequest) {
  return api<RedriveResult>('/dev-console/jobs/redrive', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function discardJobs(body: Omit<RedriveRequest, 'redriveMode'>) {
  return api<DiscardResult>('/dev-console/jobs/discard', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function searchJobLogs(filters: JobSearchFilters) {
  const sp = new URLSearchParams();
  if (filters.canonicalJob) sp.set('canonicalJob', filters.canonicalJob);
  if (filters.environment) sp.set('environment', filters.environment);
  if (filters.jobName) sp.set('jobName', filters.jobName);
  if (filters.traceId) sp.set('traceId', filters.traceId);
  if (filters.level) sp.set('level', filters.level);
  if (filters.startTime) sp.set('startTime', filters.startTime);
  if (filters.endTime) sp.set('endTime', filters.endTime);
  return api<JobLogEntry[] | { logs?: JobLogEntry[] }>(`/dev-console/jobs/logs?${sp}`);
}

export function getJobDetail(traceId: string) {
  return api<JobDetailResponse>(`/dev-console/jobs/detail/${encodeURIComponent(traceId)}`);
}

export function getJobEntity(type: EntityLookup['type'], id: number) {
  return api<Record<string, unknown>>(`/dev-console/jobs/entity/${type}/${id}`);
}

// ---------------------------------------------------------------------------
// Mobile App Deployments
// ---------------------------------------------------------------------------

type MobileAppDeploymentListParams = {
  app?: MobileAppDeploymentApp;
  deploymentType?: MobileAppDeploymentTypeFilter;
  limit?: number;
  page?: number;
  platform?: MobileAppDeploymentPlatform;
};

export function getMobileAppDeployments(params: MobileAppDeploymentListParams = {}) {
  const sp = new URLSearchParams();
  if (params.app != null) sp.set('app', params.app);
  if (params.deploymentType != null) sp.set('deploymentType', params.deploymentType);
  if (params.limit != null) sp.set('limit', String(params.limit));
  if (params.page != null) sp.set('page', String(params.page));
  if (params.platform != null) sp.set('platform', params.platform);
  const qs = sp.toString();
  return api<MobileAppDeploymentListResponse>(`/dev-console/mobile-app-deployments${qs ? `?${qs}` : ''}`);
}

// ---------------------------------------------------------------------------
// Postgres Jobs (jobs_db)
// ---------------------------------------------------------------------------

function jobsDbEnvParams(env: DeploymentEnvironment): URLSearchParams {
  const query: Required<JobsDbEnvironmentQuery> = { env };
  return new URLSearchParams(query);
}

export function getPostgresJobs(env: DeploymentEnvironment, filters: PostgresJobFilters = {}) {
  const sp = jobsDbEnvParams(env);
  if (filters.name) sp.set('name', filters.name);
  if (filters.pod) sp.set('pod', filters.pod);
  if (filters.status) sp.set('status', filters.status);
  if (filters.lane) sp.set('lane', filters.lane);
  if (filters.createdAfter) sp.set('createdAfter', filters.createdAfter);
  if (filters.createdBefore) sp.set('createdBefore', filters.createdBefore);
  if (filters.cursor) sp.set('cursor', filters.cursor);
  if (filters.limit != null) sp.set('limit', String(filters.limit));
  return api<PostgresJobListResponse>(`/dev-console/postgres-jobs?${sp}`);
}

export function getPostgresJobNames(env: DeploymentEnvironment) {
  return api<PostgresJobNamesResponse>(`/dev-console/postgres-jobs/names?${jobsDbEnvParams(env)}`);
}

export function redrivePostgresJob(env: DeploymentEnvironment, jobId: string) {
  return api<PostgresJobRedriveResult>(
    `/dev-console/postgres-jobs/${encodeURIComponent(jobId)}/redrive?${jobsDbEnvParams(env)}`,
    { method: 'POST' }
  );
}

export function redrivePostgresJobs(env: DeploymentEnvironment, jobIds: string[]) {
  return api<PostgresJobsRedriveResult>(`/dev-console/postgres-jobs/redrive?${jobsDbEnvParams(env)}`, {
    method: 'POST',
    body: JSON.stringify({ jobIds }),
  });
}

export function discardPostgresJob(env: DeploymentEnvironment, jobId: string) {
  return api<PostgresJobDiscardResult>(
    `/dev-console/postgres-jobs/${encodeURIComponent(jobId)}/discard?${jobsDbEnvParams(env)}`,
    { method: 'POST' }
  );
}

export function discardPostgresJobs(env: DeploymentEnvironment, jobIds: string[]) {
  return api<PostgresJobsDiscardResult>(`/dev-console/postgres-jobs/discard?${jobsDbEnvParams(env)}`, {
    method: 'POST',
    body: JSON.stringify({ jobIds }),
  });
}

// ---------------------------------------------------------------------------
// Job Schedules (jobs_db)
// ---------------------------------------------------------------------------

export function getJobSchedules(env: DeploymentEnvironment) {
  return api<JobScheduleListResponse>(`/dev-console/job-schedules?${jobsDbEnvParams(env)}`);
}

export function setJobScheduleEnabled(env: DeploymentEnvironment, body: JobScheduleSetEnabledRequest) {
  return api<JobScheduleSetEnabledResponse>(`/dev-console/job-schedules/set-enabled?${jobsDbEnvParams(env)}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Patient announcement banner (Statsig)
// ---------------------------------------------------------------------------

export function getPatientAnnouncementBanner() {
  return api<PatientAnnouncementBannerConfigResponse>('/dev-console/patient-announcement-banner');
}

export function updatePatientAnnouncementBanner(body: PatientAnnouncementBannerUpdateRequest) {
  return api<PatientAnnouncementBannerUpdateResponse>('/dev-console/patient-announcement-banner', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Provider announcement banner (Statsig)
// ---------------------------------------------------------------------------

export function getProviderAnnouncementBanner() {
  return api<ProviderAnnouncementBannerConfigResponse>('/dev-console/provider-announcement-banner');
}

export function updateProviderAnnouncementBanner(body: ProviderAnnouncementBannerUpdateRequest) {
  return api<ProviderAnnouncementBannerUpdateResponse>('/dev-console/provider-announcement-banner', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Workflows
// ---------------------------------------------------------------------------

export function getWorkflowRuns(params: { days?: number; query?: string; workflowId?: string }) {
  const sp = new URLSearchParams();
  if (params.days != null) sp.set('days', String(params.days));
  if (params.query?.trim()) sp.set('query', params.query.trim());
  if (params.workflowId?.trim()) sp.set('workflowId', params.workflowId.trim());
  return api<WorkflowRunsResponse>(`/dev-console/workflows/runs?${sp}`);
}

export function getWorkflowRun(runId: string, params: { days?: number } = {}) {
  const sp = new URLSearchParams();
  if (params.days != null) sp.set('days', String(params.days));
  const qs = sp.toString();
  return api<WorkflowRunDetailResponse>(
    `/dev-console/workflows/runs/${encodeURIComponent(runId)}${qs ? `?${qs}` : ''}`
  );
}

// ---------------------------------------------------------------------------
// Runbooks
// ---------------------------------------------------------------------------

export function lookupRunbook(canonicalJob: string) {
  return api<{ mapping: RunbookMapping | null }>(`/dev-console/runbook/lookup/${encodeURIComponent(canonicalJob)}`);
}

export function searchRunbook(canonicalJob: string) {
  return api<{ results: RunbookSearchResult[] }>(`/dev-console/runbook/search/${encodeURIComponent(canonicalJob)}`);
}

export function saveRunbookMapping(body: { canonicalJob: string; notionUrl: string; notionPageId?: string }) {
  return api<{ mapping: RunbookMapping }>('/dev-console/runbook/mapping', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function generateRunbookTemplate(body: { canonicalJob: string }) {
  return api<{ template: string }>('/dev-console/runbook/template', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function submitRunbookFeedback(body: { canonicalJob: string; vote: 'up' | 'down' }) {
  return api('/dev-console/runbook/feedback', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Devex Metrics
// ---------------------------------------------------------------------------

export function getReviewLatencySeries(params: {
  scope: DevexReviewLatencyScope;
  days: number;
  groupBy?: DevexReviewLatencyGroupBy;
  teams?: string[];
  excludeBots?: boolean;
  sizeBuckets?: DevexReviewLatencySizeBucket[];
}) {
  const sp = new URLSearchParams({ scope: params.scope, days: String(params.days) });
  if (params.groupBy) sp.set('groupBy', params.groupBy);
  if (params.teams?.length) sp.set('teams', params.teams.join(','));
  if (params.excludeBots != null) sp.set('excludeBots', String(params.excludeBots));
  if (params.sizeBuckets?.length) sp.set('sizeBuckets', params.sizeBuckets.join(','));
  return api<DevexReviewLatencyResponse>(`/dev-console/devex-metrics/review-latency?${sp}`);
}

export function getCodexFeedback(params: {
  botLogins?: string;
  days: number;
  includeBotReplies?: boolean;
  prNumbers?: string;
  reactionFilter?: DevexCodexFeedbackReactionFilter;
  states?: DevexCodexFeedbackPullRequestState[];
}) {
  const sp = new URLSearchParams({ days: String(params.days) });
  if (params.states?.length) sp.set('states', params.states.join(','));
  if (params.prNumbers?.trim()) sp.set('prNumbers', params.prNumbers.trim());
  if (params.botLogins?.trim()) sp.set('botLogins', params.botLogins.trim());
  if (params.includeBotReplies != null) sp.set('includeBotReplies', String(params.includeBotReplies));
  if (params.reactionFilter && params.reactionFilter !== 'all') sp.set('reactionFilter', params.reactionFilter);
  return api<DevexCodexFeedbackResponse>(`/dev-console/devex-metrics/codex-feedback?${sp}`);
}

// ---------------------------------------------------------------------------
// Deployments
// ---------------------------------------------------------------------------

export function getCurrentDeployments() {
  return api<DeploymentCurrentResponse>('/dev-console/deployments/current');
}

export function getDeploymentHistory(params: {
  environment?: DeploymentEnvironment;
  mode?: DeploymentMode;
  limit?: number;
  cursor?: string;
}) {
  const sp = new URLSearchParams();
  if (params.environment) sp.set('environment', params.environment);
  if (params.mode) sp.set('mode', params.mode);
  if (params.limit != null) sp.set('limit', String(params.limit));
  if (params.cursor) sp.set('cursor', params.cursor);
  const query = sp.toString();
  return api<DeploymentHistoryResponse>(`/dev-console/deployments${query ? `?${query}` : ''}`);
}

export function getDeploymentStats(params: {
  days: DeploymentStatsDays;
  environment?: DeploymentEnvironment | 'all';
  mode?: DeploymentMode | 'all';
}) {
  const sp = new URLSearchParams({ days: String(params.days) });
  if (params.environment != null && params.environment !== 'all') {
    sp.set('environment', params.environment);
  }
  if (params.mode != null && params.mode !== 'all') {
    sp.set('mode', params.mode);
  }
  return api<DeploymentStatsResponse>(`/dev-console/deployments/stats?${sp}`);
}

export function rollbackDeployment(body: DeploymentRollbackRequest) {
  return api<DeploymentRollbackResponse>('/dev-console/deployments/rollback', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function preflightDeploymentRollback(body: DeploymentRollbackPreflightRequest) {
  return api<DeploymentRollbackPreflightResponse>('/dev-console/deployments/rollback/preflight', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function cancelDeployment(body: DeploymentCancelRequest) {
  return api<DeploymentCancelResponse>('/dev-console/deployments/cancel', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getDeploymentPipelineStatus() {
  return api<DeploymentPipelineStatusResponse>('/dev-console/deployments/pipelines');
}

export function getMergeFreezeStatus() {
  return api<MergeFreezeStatusResponse>('/dev-console/deployments/merge-freeze');
}

export function setMergeFreeze(body: MergeFreezeRequest) {
  return api<MergeFreezeResponse>('/dev-console/deployments/merge-freeze', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function setDeploymentPipelines(body: DeploymentPipelineRequest) {
  return api<DeploymentPipelineResponse>('/dev-console/deployments/pipelines', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getDownstreamHealth() {
  return api<DownstreamHealthResponse>('/dev-console/downstream-health');
}

export function getEnrollmentAutomations() {
  return api<EnrollmentAutomationsResponse>('/dev-console/enrollment-automations');
}

export function updateEnrollmentAutomation(configName: string, body: EnrollmentAutomationUpdateRequest) {
  return api<EnrollmentAutomationUpdateResponse>(
    `/dev-console/enrollment-automations/${encodeURIComponent(configName)}`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}
